import { useState, useRef, useEffect, useCallback } from "react";
import { ChevronRight, BookOpen } from "lucide-react";
import RowMenu from "./RowMenu";
import { SB } from "@/config/sidebarSizing";
import { THEME } from "@/config/theme";
import LeafDocument from "./LeafDocument";
import { useViewportCtx } from "@/contexts/ViewportCtx";
import type { ApiSubject, ApiProject, ApiForge } from "@/services/projectService";

interface Props {
  subject: ApiSubject;
  project: ApiProject;
  onRename: (subjectId: string, title: string) => void;
  onDelete: (subjectId: string) => void;
  onCreateForge: (subjectId: string, title: string) => Promise<ApiForge>;
  onRenameForge: (forgeId: string, title: string) => void;
  onDeleteForge: (forgeId: string) => void;
  onForgeSelect?: () => void;
}

export default function SubjectTree({ subject, project, onRename, onDelete, onCreateForge, onRenameForge, onDeleteForge, onForgeSelect }: Props) {
  const [open, setOpen] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(subject.title);
  const [creatingForge, setCreatingForge] = useState(false);
  const [newForgeTitle, setNewForgeTitle] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const forgeInputRef = useRef<HTMLInputElement>(null);
  const { forgeId } = useViewportCtx();

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  useEffect(() => {
    if (creatingForge) forgeInputRef.current?.focus();
  }, [creatingForge]);

  useEffect(() => {
    setEditTitle(subject.title);
  }, [subject.title]);

  const startEdit = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setEditTitle(subject.title);
    setEditing(true);
  };

  const commitEdit = () => {
    setEditing(false);
    const title = editTitle.trim();
    if (title && title !== subject.title) {
      onRename(subject.id, title);
    } else {
      setEditTitle(subject.title);
    }
  };

  const startCreatingForge = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setNewForgeTitle("Untitled Forge");
    setCreatingForge(true);
    setOpen(true);
  };

  const commitForge = useCallback(async () => {
    const title = newForgeTitle.trim();
    setCreatingForge(false);
    if (!title) return;
    await onCreateForge(subject.id, title);
  }, [newForgeTitle, onCreateForge, subject.id]);

  return (
    <div className="mb-0">
      <div
        className={`w-full flex items-center ${SB.row.subject} rounded-md text-slate-400 hover:bg-white/5 hover:text-slate-100 transition-colors duration-150 ease-out cursor-pointer group`}
        onClick={() => !editing && setOpen((v) => !v)}
      >
        <ChevronRight
          size={SB.icon.chevronSubject}
          className={`shrink-0 text-slate-500 group-hover:text-slate-300 transition-all duration-150 ${open ? "rotate-90 text-slate-300" : ""}`}
        />
        <BookOpen size={SB.icon.file} className="shrink-0 text-indigo-400/80 group-hover:text-indigo-300 transition-colors duration-150" />
        {editing ? (
          <input
            ref={inputRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitEdit();
              if (e.key === "Escape") { setEditing(false); setEditTitle(subject.title); }
            }}
            onClick={(e) => e.stopPropagation()}
            className={`flex-1 ${SB.text.subject} ${SB.font.sidebar} ${THEME.sidebar.input} rounded px-1 py-0 min-w-0`}
          />
        ) : (
          <span
            className={`${SB.text.subject} ${SB.font.sidebar} font-semibold text-slate-300 group-hover:text-slate-50 truncate flex-1 transition-colors duration-150 select-text`}
            onDoubleClick={startEdit}
            title="Double-click to rename"
          >
            {subject.title}
          </span>
        )}
        <RowMenu items={[
          { label: "Rename",    onClick: () => startEdit() },
          { label: "New Forge", onClick: () => startCreatingForge() },
          { label: "Delete",    onClick: () => onDelete(subject.id), danger: true },
        ]} />
      </div>

      {open && (
        <div className={`${SB.row.forgeIndent} ${SB.row.subjectForgeGap} ${SB.row.forgeGap}`}>
          {creatingForge && (
            <div className="px-2 py-1">
              <input
                ref={forgeInputRef}
                value={newForgeTitle}
                onChange={(e) => setNewForgeTitle(e.target.value)}
                onBlur={commitForge}
                onKeyDown={(e) => {
                  if (e.key === "Enter") commitForge();
                  if (e.key === "Escape") setCreatingForge(false);
                }}
                className={`w-full ${SB.text.forge} ${SB.font.sidebar} ${THEME.sidebar.input} rounded px-1.5 py-0.5`}
              />
            </div>
          )}
          {subject.forges.map((forge) => (
            <LeafDocument
              key={forge.id}
              forge={forge}
              project={project}
              siblingForges={subject.forges}
              isActive={forgeId === forge.id}
              onRename={onRenameForge}
              onDelete={onDeleteForge}
              onSelect={onForgeSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}
